import { queryOptions, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';
import { hydraUiApi } from '@/services/api';
import type { QueryConfig } from '../shared/react-query-types';
import { requireWorkspaceId } from '../shared/workspace';
import {
  facebookLeadAdsCredentialKey,
  facebookLeadAdsFormKeys,
  parseFacebookLeadAdsCredential,
  parseFacebookLeadAdsForm,
  type FacebookLeadAdsCredential,
  type FacebookLeadAdsForm,
} from './entity';

const STALE_TIME = 60_000;

/**
 * @public The Facebook Lead Ads credential, or `null` when not connected. The
 * not-found error is swallowed and treated as "not connected".
 */
export async function getFacebookLeadAdsCredential(workspaceId: number): Promise<FacebookLeadAdsCredential | null> {
  try {
    const data = await hydraUiApi.facebookLeadAds.getFacebookLeadAdsCredentials({
      params: { path: { environment_id: String(workspaceId) } },
    });
    return data.facebook_lead_ads_credential == null
      ? null
      : parseFacebookLeadAdsCredential(data.facebook_lead_ads_credential);
  } catch {
    return null;
  }
}

/** @public */
export const getFacebookLeadAdsCredentialQueryOptions = (workspaceId: number) =>
  queryOptions({
    queryKey: facebookLeadAdsCredentialKey(workspaceId),
    queryFn: () => getFacebookLeadAdsCredential(workspaceId),
    staleTime: STALE_TIME,
  });

/** @public */
export const useFacebookLeadAdsCredential = ({
  queryConfig,
}: { queryConfig?: QueryConfig<typeof getFacebookLeadAdsCredentialQueryOptions> } = {}) => {
  const workspaceId = requireWorkspaceId();
  return useQuery({ ...getFacebookLeadAdsCredentialQueryOptions(workspaceId), ...queryConfig });
};

/** @public The workspace's Facebook Lead Ads forms (empty when none / not connected). */
export async function getFacebookLeadAdsForms(workspaceId: number, qc: QueryClient): Promise<FacebookLeadAdsForm[]> {
  try {
    const data = await hydraUiApi.facebookLeadAds.getFacebookLeadAdsForms({
      params: { path: { environment_id: String(workspaceId) } },
    });
    const parsed = (data.facebook_lead_ads_forms ?? []).map(parseFacebookLeadAdsForm);
    for (const f of parsed) {
      qc.setQueryData(facebookLeadAdsFormKeys.detail(workspaceId, f.id), f);
    }
    return parsed;
  } catch {
    return [];
  }
}

/** @public */
export const getFacebookLeadAdsFormsQueryOptions = (workspaceId: number, qc: QueryClient) =>
  queryOptions({
    queryKey: facebookLeadAdsFormKeys.list(workspaceId, undefined),
    queryFn: () => getFacebookLeadAdsForms(workspaceId, qc),
    staleTime: STALE_TIME,
  });

/** @public */
export const useFacebookLeadAdsForms = ({
  queryConfig,
}: { queryConfig?: QueryConfig<typeof getFacebookLeadAdsFormsQueryOptions> } = {}) => {
  const workspaceId = requireWorkspaceId();
  const qc = useQueryClient();
  return useQuery({ ...getFacebookLeadAdsFormsQueryOptions(workspaceId, qc), ...queryConfig });
};
